import { useEffect, useRef, useState } from 'react';
import { Download, Lock, Info, RotateCcw, Loader2 } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/ui/PageHeader';
import { api, ApiError, getAuthToken, resolveApiUrl } from '../services/apiClient';
import { CreateUnlockResponse, EntitlementDownloadResponse, MyLibraryItemDto, MyLibraryResponseDto } from '@primetime/shared/apiContracts';
import { useAuth } from '../features/auth/AuthContext';
import { dispatchAccountStateSync, onAccountStateSync } from '../features/account/accountStateSync';
import { useAccountCredits } from '../features/account/useAccountCredits';
import { formatRemainingDownloads } from '../features/account/libraryDisplay';

function displayVersionLabel(item: MyLibraryItemDto) {
  const editorName = item.editorName?.trim();
  const versionLabel = item.versionLabel?.trim();
  if (editorName && versionLabel) {
    if (editorName.includes(versionLabel)) return editorName;
    return `${editorName} ${versionLabel}`;
  }
  return editorName || versionLabel || '';
}

export function MyLibrary() {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const { creditStateReady, markInsufficientCredits, requiresSubscribe } = useAccountCredits(user?.id, authLoading);
  const [libraryItems, setLibraryItems] = useState<MyLibraryItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pendingRenewal, setPendingRenewal] = useState<MyLibraryItemDto | null>(null);
  const [renewingId, setRenewingId] = useState<string | null>(null);
  const libraryRequestRef = useRef(0);

  async function fetchLibrary(showLoading = true) {
    const requestId = ++libraryRequestRef.current;
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const libraryData = await api<MyLibraryResponseDto>('/api/account/library');
      if (requestId !== libraryRequestRef.current) return;
      setLibraryItems(libraryData.items);
    } catch (err) {
      if (requestId !== libraryRequestRef.current) return;
      console.error('Failed to fetch library:', err);
      setError('Failed to load your library. Please try again.');
    } finally {
      if (showLoading && requestId === libraryRequestRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    void fetchLibrary();
  }, [user]);

  useEffect(() => {
    return onAccountStateSync(({ refreshLibrary }) => {
      if (!refreshLibrary || !user) return;
      void fetchLibrary();
    });
  }, [user]);

  async function downloadItem(item: MyLibraryItemDto) {
    setDownloadError(null);
    setDownloadingId(item.entitlementId);
    try {
      if (item.assetGroupId && item.trackVersionId) {
        const token = getAuthToken();
        const response = await fetch(resolveApiUrl(`/api/downloads/${encodeURIComponent(item.entitlementId)}`), {
          method: 'GET',
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        if (!response.ok) {
          let message = response.statusText;
          try {
            const body = await response.json() as { error?: string };
            message = body.error || message;
          } catch {
            // Preserve the HTTP status when the customer-facing error is not JSON.
          }
          throw new ApiError(response.status, message);
        }
        const contentType = response.headers.get('content-type')?.toLowerCase() || '';
        const anchor = document.createElement('a');
        if (contentType.includes('application/json')) {
          const result = await response.json() as { url?: string };
          if (!result.url) throw new ApiError(502, 'Unable to start secure download.');
          anchor.href = result.url;
        } else {
          const blob = await response.blob();
          anchor.href = URL.createObjectURL(blob);
        }
        anchor.download = item.downloadFilename || `${item.artist} - ${item.title}.mp4`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        if (anchor.href.startsWith('blob:')) URL.revokeObjectURL(anchor.href);
      } else {
        const result = await api<EntitlementDownloadResponse>(`/api/downloads/${item.entitlementId}`, { method: 'POST' });
        window.location.assign(result.url);
      }
      await fetchLibrary(false);
    } catch (err) {
      setDownloadError(err instanceof ApiError ? err.message : 'Unable to start secure download.');
    } finally {
      setDownloadingId(null);
    }
  }

  async function downloadEntitlement(entitlementId: string) {
    const item = libraryItems.find((candidate) => candidate.entitlementId === entitlementId);
    if (item) await downloadItem(item);
  }

  function queueRenewal(item: MyLibraryItemDto) {
    if (!item.assetGroupId || !item.trackVersionId) return;
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const creditCost = item.creditCost ?? 1;
    if (!creditStateReady) return;
    if (requiresSubscribe(item.trackVersionId, creditCost)) {
      navigate('/pricing');
      return;
    }
    setDownloadError(null);
    setPendingRenewal(item);
  }

  async function confirmRenewal() {
    const target = pendingRenewal;
    if (!target || !user || !target.assetGroupId || !target.trackVersionId || !creditStateReady || renewingId) return;

    const creditCost = target.creditCost ?? 1;
    setRenewingId(target.entitlementId);
    setDownloadError(null);
    try {
      const result = await api<CreateUnlockResponse>('/api/unlocks', {
        method: 'POST',
        body: JSON.stringify({
          productType: 'TRACK_VERSION',
          assetGroupId: target.assetGroupId,
          trackVersionId: target.trackVersionId,
          idempotencyKey: `library-renewal-${target.trackVersionId}-${crypto.randomUUID()}`
        })
      });

      dispatchAccountStateSync({ updatedCredits: result.updatedCredits, refreshLibrary: true });
      const renewedItem: MyLibraryItemDto = {
        ...target,
        downloadLimit: target.downloadLimit + 5,
        remainingDownloads: target.remainingDownloads + 5,
        downloadAvailable: true,
        status: 'Available'
      };
      setLibraryItems((current) => current.map((item) => item.entitlementId === target.entitlementId ? renewedItem : item));
      setPendingRenewal(null);
      await downloadItem(renewedItem);
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) {
        await markInsufficientCredits(target.trackVersionId, creditCost);
        await refreshAuth();
      }
      setDownloadError(error instanceof ApiError ? error.message : 'Unable to renew this download allowance.');
    } finally {
      setRenewingId(null);
    }
  }

  return (
    <section className='container-shell py-14'>
      <PageHeader
        title='My Library'
        subtitle='Download Edits'
      />

      {/* Policy Notice */}
      <div className='mb-10 flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-6'>
        <div className='mt-1 shrink-0 text-prime-red'>
          <Info size={24} />
        </div>
        <div className='text-sm leading-relaxed text-white/70'>
          <p className='font-bold text-white mb-1 uppercase tracking-wider'>Download Policy</p>
          Each unlocked edit includes five secure downloads per PrimeTime Credit. We allow multiple downloads because mistakes happen. Files can fail or browsers can crash. When an allowance is exhausted, an eligible version can be renewed for another five downloads with one PrimeTime Credit. We can detect anomalies like unusual IP addresses or device fingerprints. If we detect that you have shared your account, we will suspend your account.
        </div>
      </div>

      {downloadError && (
        <div className='mb-6 rounded-2xl border border-prime-red/30 bg-prime-red/10 px-5 py-4 text-sm font-bold text-red-100'>
          {downloadError}
        </div>
      )}

      <div className='glass overflow-hidden rounded-3xl'>
        <div className='overflow-x-auto custom-scrollbar'>
          <table className='min-w-[1180px] w-full table-fixed'>
            <colgroup>
              <col className='w-[132px]' />
              <col className='w-[300px]' />
              <col className='w-[140px]' />
              <col className='w-[70px]' />
              <col className='w-[70px]' />
              <col className='w-[90px]' />
              <col className='w-[130px]' />
              <col className='w-[110px]' />
              <col className='w-[138px]' />
            </colgroup>
            <thead className='border-b border-white/10 bg-white/[0.035] text-left text-[10px] font-black uppercase tracking-widest text-white/30'>
              <tr>
                <th scope='col' className='py-4 pl-8 pr-3 font-black'>Date Unlocked</th>
                <th scope='col' className='px-3 py-4 font-black'>Track / Version</th>
                <th scope='col' className='px-3 py-4 font-black'>Genre / Style</th>
                <th scope='col' className='px-3 py-4 text-center font-black'>BPM</th>
                <th scope='col' className='px-3 py-4 text-center font-black'>Key</th>
                <th scope='col' className='px-3 py-4 text-center font-black'>Credits</th>
                <th scope='col' className='px-3 py-4 text-center font-black'>Downloads Remaining</th>
                <th scope='col' className='px-3 py-4 text-center font-black'>Status</th>
                <th scope='col' className='py-4 pl-3 pr-8 text-right font-black'>Action</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-white/5'>
              {loading ? (
                <tr>
                  <td colSpan={9} className='px-8 py-10 text-center text-white/50'>Loading your library...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className='px-8 py-10 text-center text-prime-red'>{error}</td>
                </tr>
              ) : libraryItems.length === 0 ? (
                <tr>
                  <td colSpan={9} className='px-8 py-12 text-center'>
                    <div className='flex flex-col items-center justify-center gap-4'>
                      <p className='text-white/50'>No unlocked items yet.</p>
                      <Link to='/crates' className='btn-primary rounded-xl px-5 py-2 text-xs font-bold'>
                        Browse Crates
                      </Link>
                    </div>
                  </td>
                </tr>
              ) : (
                libraryItems.map((item) => (
                  <tr key={item.entitlementId} className='hover:bg-white/[0.02] transition-colors'>
                    <td className='py-6 pl-8 pr-3 align-middle text-xs font-medium text-white/40'>
                      {new Date(item.unlockedAt).toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}
                    </td>

                    <td className='px-3 py-6 align-middle'>
                      <div className='min-w-0 max-w-full'>
                        <div className='truncate font-bold text-white'>{item.title}</div>
                        <div className='mt-0.5 flex min-w-0 items-center gap-2'>
                          <span className='min-w-0 truncate text-xs text-white/40'>{item.artistDisplay || item.artist}</span>
                          <span className='h-1 w-1 shrink-0 rounded-full bg-white/20' />
                          <span className='shrink-0 truncate text-[10px] font-black uppercase text-prime-red'>{displayVersionLabel(item)}</span>
                        </div>
                      </div>
                    </td>

                    <td className='px-3 py-6 align-middle text-xs font-medium text-white/60'>
                      <div className='min-w-0 max-w-full'>
                        <div className='truncate'>{item.genre || '-'}</div>
                        {item.style && <div className='mt-0.5 truncate text-white/35'>{item.style}</div>}
                      </div>
                    </td>

                    <td className='px-3 py-6 text-center align-middle text-sm font-black text-white/80'>{item.bpm ?? '-'}</td>

                    <td className='px-3 py-6 text-center align-middle text-sm font-black text-white/80'>{item.key || '-'}</td>

                    <td className='px-3 py-6 text-center align-middle text-sm font-black text-white/80'>
                      {typeof item.creditCost === 'number' ? `${item.creditCost}` : '-'}
                    </td>

                    <td className='px-3 py-6 text-center align-middle text-sm font-bold text-white/60'>
                      {formatRemainingDownloads(item.remainingDownloads)}
                    </td>

                    <td className='px-3 py-6 text-center align-middle'>
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider ${
                        item.status === 'Available' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-prime-red/10 text-prime-red'
                      }`}>
                        {item.status}
                      </span>
                    </td>

                    <td className='py-6 pl-3 pr-8 text-right align-middle'>
                      {item.downloadAvailable && item.remainingDownloads > 0 ? (
                        <button
                          type='button'
                          onClick={() => downloadEntitlement(item.entitlementId)}
                          disabled={downloadingId === item.entitlementId}
                          className='btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          <Download size={14} /> {downloadingId === item.entitlementId ? 'Preparing...' : 'Download'}
                        </button>
                      ) : item.status === 'Locked' && item.assetGroupId && item.trackVersionId ? (
                        <button
                          type='button'
                          onClick={() => queueRenewal(item)}
                          disabled={!creditStateReady || Boolean(renewingId) || downloadingId === item.entitlementId}
                          className='btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          {renewingId === item.entitlementId ? <Loader2 size={14} className='animate-spin' /> : <RotateCcw size={14} />}
                          {!creditStateReady
                            ? 'Checking Credits...'
                            : requiresSubscribe(item.trackVersionId, item.creditCost ?? 1)
                              ? 'Subscribe'
                              : 'Renew 1 Credit'}
                        </button>
                      ) : (
                        <button disabled className='inline-flex cursor-not-allowed items-center gap-2 rounded-xl border border-white/5 bg-white/5 px-4 py-2 text-xs text-white/20'>
                          <Lock size={14} /> Unavailable
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className='mt-8 text-center'>
        <p className='text-xs text-white/30'>Need more downloads? <button className='text-prime-red hover:underline font-bold'>Contact support</button></p>
      </div>

      {pendingRenewal && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm' role='dialog' aria-modal='true' aria-labelledby='library-renewal-title' aria-describedby='library-renewal-description'>
          <div className='glass w-full max-w-[460px] rounded-2xl border border-white/10 p-5 shadow-2xl'>
            <p className='text-[10px] font-black uppercase tracking-[0.2em] text-prime-red'>PRIMETIME CREDITS</p>
            <h2 id='library-renewal-title' className='mt-2 text-xl font-black text-white'>GET 5 DOWNLOADS</h2>
            <div className='mt-3 min-w-0'>
              <p className='break-words text-[15px] font-black leading-5 text-white'>{pendingRenewal.title}</p>
              <p className='mt-1 break-words text-[12px] leading-5 text-white/60'>
                {pendingRenewal.artistDisplay || pendingRenewal.artist}
                <span className='mx-2 text-white/30'>•</span>
                <span className='font-black uppercase text-prime-red'>
                  {pendingRenewal.editorName?.trim() || pendingRenewal.versionLabel?.trim() || pendingRenewal.downloadVariant || ''}
                </span>
              </p>
            </div>
            <div className='mt-3 rounded-xl border border-white/15 bg-white/[0.04] p-3 text-xs'>
              <div className='flex items-center justify-between gap-4'>
                <span className='text-white/55'>Version</span>
                <span className='font-black text-white'>{pendingRenewal.downloadVariant || pendingRenewal.versionLabel}</span>
              </div>
              <div className='mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2'>
                <span className='text-white/55'>Cost</span>
                <span className='font-black text-white'>{pendingRenewal.creditCost ?? 1} PrimeTime {(pendingRenewal.creditCost ?? 1) === 1 ? 'Credit' : 'Credits'}</span>
              </div>
              <div className='mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2'>
                <span className='text-white/55'>Downloads included</span>
                <span className='font-black text-white'>5</span>
              </div>
            </div>
            <p id='library-renewal-description' className='mt-3 text-[12px] leading-5 text-white/60'>
              Your MP4 will download automatically after confirmation. Your remaining 4 downloads will be available in My Library.
            </p>
            {downloadError && <p role='alert' className='mt-4 text-sm font-bold text-red-200'>{downloadError}</p>}
            <div className='mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
              <button
                type='button'
                onClick={() => {
                  if (renewingId) return;
                  setPendingRenewal(null);
                  setDownloadError(null);
                }}
                disabled={Boolean(renewingId)}
                className='w-full rounded-lg border border-white/15 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white/70 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto'
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={() => void confirmRenewal()}
                disabled={Boolean(renewingId)}
                className='btn-primary inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto'
              >
                {renewingId ? <Loader2 size={16} className='animate-spin' /> : null}
                {renewingId ? 'Renewing...' : 'Confirm & Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
